import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { LangContext } from '../i18n/LangContext';
import { AuthContext } from '../contexts/AuthContext';
import { getLogoUrl, getProfilePhotoUrl } from '../config';
import logger from '../utils/logger';

const MyServiceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  useContext(LangContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadRequests();
  }, [user, navigate]);

  const loadRequests = async () => {
    try {
      const res = await api.get('/service-requests/my');
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error(err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (statut) => {
    const statusMap = {
      'en_attente': { 
        label: 'En attente', 
        icon: '⏳',
        color: 'bg-amber-50 text-amber-700 border-amber-200', 
        dotColor: 'bg-amber-400',
        gradient: 'from-amber-400 to-orange-400'
      },
      'en_cours': { 
        label: 'En cours', 
        icon: '🔄',
        color: 'bg-blue-50 text-blue-700 border-blue-200', 
        dotColor: 'bg-blue-400',
        gradient: 'from-blue-400 to-cyan-400'
      },
      'paiement_attente': { 
        label: 'Paiement attendu', 
        icon: '💳',
        color: 'bg-orange-50 text-orange-700 border-orange-200', 
        dotColor: 'bg-orange-400',
        gradient: 'from-orange-400 to-red-400'
      },
      'paiement_recu': { 
        label: 'Paiement reçu', 
        icon: '✅',
        color: 'bg-teal-50 text-teal-700 border-teal-200', 
        dotColor: 'bg-teal-400',
        gradient: 'from-teal-400 to-emerald-400'
      },
      'fourniture': { 
        label: 'En livraison', 
        icon: '📦',
        color: 'bg-purple-50 text-purple-700 border-purple-200', 
        dotColor: 'bg-purple-400',
        gradient: 'from-purple-400 to-pink-400'
      },
      'termine': { 
        label: 'Terminé', 
        icon: '🎉',
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200', 
        dotColor: 'bg-emerald-400',
        gradient: 'from-emerald-400 to-green-400'
      },
      'annule': { 
        label: 'Annulé', 
        icon: '❌',
        color: 'bg-gray-50 text-gray-600 border-gray-200', 
        dotColor: 'bg-gray-400',
        gradient: 'from-gray-400 to-gray-500'
      },
      'refuse': { 
        label: 'Refusé', 
        icon: '⛔',
        color: 'bg-red-50 text-red-700 border-red-200', 
        dotColor: 'bg-red-400',
        gradient: 'from-red-400 to-rose-400'
      }
    };
    return statusMap[statut] || { label: statut, icon: '❓', color: 'bg-gray-50 text-gray-600 border-gray-200', dotColor: 'bg-gray-400', gradient: 'from-gray-400 to-gray-500' };
  };

  const getPriorityInfo = (priorite) => {
    const priorities = {
      'basse': { label: 'Basse', color: 'text-gray-500', bg: 'bg-gray-100' },
      'normale': { label: 'Normale', color: 'text-blue-600', bg: 'bg-blue-50' },
      'haute': { label: 'Haute', color: 'text-orange-600', bg: 'bg-orange-50' },
      'urgente': { label: 'Urgente', color: 'text-red-600', bg: 'bg-red-50' }
    };
    return priorities[priorite] || { label: priorite, color: 'text-gray-500', bg: 'bg-gray-100' };
  };

  // Filtrage et tri
  const filteredRequests = useMemo(() => {
    let filtered = [...requests];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(r =>
        r.titre?.toLowerCase().includes(searchLower) ||
        r.service_titre?.toLowerCase().includes(searchLower) ||
        r.description?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.statut_nom === statusFilter);
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.date_demande);
      const dateB = new Date(b.date_demande);
      if (sortBy === 'recent') return dateB - dateA;
      if (sortBy === 'oldest') return dateA - dateB;
      return 0;
    });

    return filtered;
  }, [requests, search, statusFilter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRequests.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRequests, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, sortBy]);

  // Stats
  const stats = useMemo(() => {
    return {
      total: requests.length,
      en_attente: requests.filter(r => r.statut_nom === 'en_attente').length,
      en_cours: requests.filter(r => ['en_cours', 'paiement_attente', 'paiement_recu', 'fourniture'].includes(r.statut_nom)).length,
      termines: requests.filter(r => r.statut_nom === 'termine').length,
      messages_non_lus: requests.reduce((acc, r) => acc + (r.messages_non_lus || 0), 0)
    };
  }, [requests]);

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-indigo-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
            <div className="absolute inset-3 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <span className="text-white text-xl">📋</span>
            </div>
          </div>
          <p className="text-indigo-600 font-medium text-lg">Chargement de vos demandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <span className="text-2xl">📋</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold">Mes demandes de services</h1>
              </div>
              <p className="text-indigo-100 text-sm md:text-base">
                Suivez l'évolution de vos demandes en temps réel
              </p>
            </div>
            <Link
              to="/services"
              className="group flex items-center justify-center gap-2 px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <span className="text-xl group-hover:scale-110 transition-transform">✨</span>
              Nouvelle demande
            </Link>
          </div>

          {/* Stats Cards - Dans le header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">📊</span>
                </div>
                <div>
                  <div className="text-3xl font-bold">{stats.total}</div>
                  <div className="text-indigo-200 text-sm">Total</div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-400/30 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">⏳</span>
                </div>
                <div>
                  <div className="text-3xl font-bold">{stats.en_attente}</div>
                  <div className="text-indigo-200 text-sm">En attente</div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-400/30 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">🔄</span>
                </div>
                <div>
                  <div className="text-3xl font-bold">{stats.en_cours}</div>
                  <div className="text-indigo-200 text-sm">En cours</div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all relative">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-400/30 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">🎉</span>
                </div>
                <div>
                  <div className="text-3xl font-bold">{stats.termines}</div>
                  <div className="text-indigo-200 text-sm">Terminés</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        
        {/* Notification messages non lus */}
        {stats.messages_non_lus > 0 && (
          <div className="mb-6 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-2xl p-4 shadow-lg shadow-purple-500/20 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-xl">💬</span>
              </div>
              <div className="flex-1">
                <p className="font-semibold">Vous avez {stats.messages_non_lus} message(s) non lu(s)</p>
                <p className="text-sm text-purple-100">Consultez vos demandes pour voir les nouveaux messages</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Rechercher une demande..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white transition-all"
              />
            </div>

            {/* Filters group */}
            <div className="flex flex-wrap gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 appearance-none cursor-pointer min-w-[160px]"
              >
                <option value="all">📁 Tous les statuts</option>
                <option value="en_attente">⏳ En attente</option>
                <option value="en_cours">🔄 En cours</option>
                <option value="paiement_attente">💳 Paiement attendu</option>
                <option value="paiement_recu">✅ Paiement reçu</option>
                <option value="fourniture">📦 En livraison</option>
                <option value="termine">🎉 Terminé</option>
                <option value="annule">❌ Annulé</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 appearance-none cursor-pointer"
              >
                <option value="recent">🕐 Plus récentes</option>
                <option value="oldest">📆 Plus anciennes</option>
              </select>

              {/* View toggle */}
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'cards' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-600">
            <span className="font-bold text-indigo-600">{filteredRequests.length}</span> demande{filteredRequests.length > 1 ? 's' : ''} trouvée{filteredRequests.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* Requests Grid/List */}
        {filteredRequests.length > 0 ? (
          <div className={viewMode === 'cards' ? 'grid md:grid-cols-2 gap-4' : 'space-y-3'}>
            {paginatedRequests.map(request => {
              const statusInfo = getStatusInfo(request.statut_nom);
              const priorityInfo = getPriorityInfo(request.priorite);
              
              if (viewMode === 'list') {
                return (
                  <Link
                    key={request.id}
                    to={`/my-service-requests/${request.id}`}
                    className="group block bg-white rounded-xl shadow-sm hover:shadow-md border border-gray-100 hover:border-indigo-200 transition-all duration-300 overflow-hidden"
                  >
                    <div className="flex items-center p-4 gap-4">
                      {/* Logo */}
                      <div className="flex-shrink-0">
                        {request.logo ? (
                          <img 
                            src={getLogoUrl(request.logo)}
                            alt={request.service_titre}
                            className="w-14 h-14 object-contain bg-gray-50 rounded-xl p-1"
                          />
                        ) : (
                          <div className={`w-14 h-14 bg-gradient-to-br ${statusInfo.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                            <span className="text-white text-xl">{statusInfo.icon}</span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                            {request.titre}
                          </h3>
                          {request.messages_non_lus > 0 && (
                            <span className="flex-shrink-0 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                              {request.messages_non_lus}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{request.service_titre}</p>
                        {/* Prestataire info en ligne */}
                        {request.prestataire_nom && (
                          <div className="flex items-center gap-2 mt-1">
                            {request.prestataire_photo ? (
                              <img 
                                src={getProfilePhotoUrl(request.prestataire_photo)}
                                alt={request.prestataire_nom}
                                className="w-5 h-5 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white text-xs font-bold">
                                {request.prestataire_nom?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                            <span className="text-xs text-gray-500">{request.prestataire_nom}</span>
                            {request.prestataire_telephone && (
                              <span className="text-xs text-gray-400">• 📞 {request.prestataire_telephone}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Status & Amount */}
                      <div className="flex-shrink-0 text-right">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${statusInfo.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dotColor}`}></span>
                          {statusInfo.label}
                        </span>
                        {request.montant_final && (
                          <p className="mt-1 font-bold text-indigo-600">
                            {Number(request.montant_final).toLocaleString()} XOF
                          </p>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="flex-shrink-0 text-gray-300 group-hover:text-indigo-500 transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                );
              }
              
              // Cards view
              return (
                <Link
                  key={request.id}
                  to={`/my-service-requests/${request.id}`}
                  className="group block bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 hover:border-indigo-200 transition-all duration-300 overflow-hidden"
                >
                  {/* Status bar */}
                  <div className={`h-1.5 bg-gradient-to-r ${statusInfo.gradient}`}></div>
                  
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-start gap-4 mb-4">
                      {request.logo ? (
                        <img 
                          src={getLogoUrl(request.logo)}
                          alt={request.service_titre}
                          className="w-14 h-14 object-contain bg-gray-50 rounded-xl p-1 group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className={`w-14 h-14 bg-gradient-to-br ${statusInfo.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform`}>
                          <span className="text-white text-2xl">{statusInfo.icon}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                            {request.titre}
                          </h3>
                          {request.messages_non_lus > 0 && (
                            <span className="flex-shrink-0 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-bounce">
                              {request.messages_non_lus}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{request.service_titre}</p>
                        {request.categorie && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs rounded-md">
                            {request.categorie}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[40px]">
                      {request.description}
                    </p>

                    {/* Prestataire Info */}
                    {request.prestataire_nom && (
                      <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-3 mb-4">
                        <div className="flex items-center gap-3">
                          {request.prestataire_photo ? (
                            <img 
                              src={getProfilePhotoUrl(request.prestataire_photo)}
                              alt={request.prestataire_nom}
                              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white font-bold shadow">
                              {request.prestataire_nom?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 text-sm truncate">
                              👤 {request.prestataire_nom}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {request.prestataire_telephone && (
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  📞 {request.prestataire_telephone}
                                </span>
                              )}
                              {request.prestataire_email && (
                                <span className="text-xs text-gray-500 flex items-center gap-1 truncate">
                                  ✉️ {request.prestataire_email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Service Info */}
                    {(request.service_tarif || request.service_delai) && (
                      <div className="flex gap-2 mb-4">
                        {request.service_tarif && (
                          <div className="flex-1 bg-emerald-50 rounded-lg p-2 text-center">
                            <p className="text-xs text-emerald-600">💰 Tarif</p>
                            <p className="font-bold text-emerald-700 text-sm">{Number(request.service_tarif).toLocaleString()} XOF</p>
                          </div>
                        )}
                        {request.service_delai && (
                          <div className="flex-1 bg-blue-50 rounded-lg p-2 text-center">
                            <p className="text-xs text-blue-600">⏱️ Délai</p>
                            <p className="font-bold text-blue-700 text-sm">{request.service_delai}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${statusInfo.color}`}>
                        <span className={`w-2 h-2 rounded-full ${statusInfo.dotColor} animate-pulse`}></span>
                        {statusInfo.label}
                      </span>
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${priorityInfo.bg} ${priorityInfo.color}`}>
                        {priorityInfo.label}
                      </span>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(request.date_demande)}
                      </div>
                      
                      {request.montant_final ? (
                        <div className="text-right">
                          <p className="text-lg font-bold text-indigo-600">
                            {Number(request.montant_final).toLocaleString()} <span className="text-xs">XOF</span>
                          </p>
                          {request.montant_paye > 0 && request.montant_paye < request.montant_final && (
                            <p className="text-xs text-emerald-600">
                              ✓ {Number(request.montant_paye).toLocaleString()} payé
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 flex items-center gap-1 group-hover:text-indigo-500 transition-colors">
                          Voir détails
                          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          /* Empty state */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-5xl">📋</span>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {search || statusFilter !== 'all' ? 'Aucun résultat' : 'Aucune demande'}
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {search || statusFilter !== 'all' 
                ? 'Essayez de modifier vos filtres de recherche' 
                : 'Vous n\'avez pas encore fait de demande de service. Découvrez nos services et faites votre première demande !'}
            </p>
            {!search && statusFilter === 'all' && (
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <span className="text-xl">🚀</span>
                Découvrir nos services
              </Link>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            {/* Info */}
            <p className="text-sm text-gray-600">
              Affichage de <span className="font-semibold text-indigo-600">{(currentPage - 1) * itemsPerPage + 1}</span> à{' '}
              <span className="font-semibold text-indigo-600">{Math.min(currentPage * itemsPerPage, filteredRequests.length)}</span> sur{' '}
              <span className="font-semibold">{filteredRequests.length}</span> demandes
            </p>
            
            {/* Navigation */}
            <div className="flex items-center gap-2">
              {/* Première page */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Première page"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Page précédente */}
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Page précédente"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Numéros de page */}
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPages <= 5) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, index, arr) => (
                    <React.Fragment key={page}>
                      {index > 0 && arr[index - 1] !== page - 1 && (
                        <span className="px-1 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[40px] h-10 rounded-lg font-medium transition-all ${
                          currentPage === page
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                            : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }
              </div>
              
              {/* Page suivante */}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Page suivante"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {/* Dernière page */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Dernière page"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyServiceRequests;
